import DeleteIcon from "@mui/icons-material/Delete";
import { Button, IconButton, MenuItem, Stack, TextField, Typography } from "@mui/material";

import type { Condition } from "./useData";

const OPS = ["==", "!=", ">", ">=", "<", "<=", "in", "not_in", "contains", "startswith", "is_empty", "is_not_empty", "between"];

/** Visual AND-list of {field, op, value} clauses shared by rules, policies and assignment rules (CF-0108). */
export function ConditionEditor({ value, onChange, fieldHints = [] }: { value: Condition[]; onChange: (c: Condition[]) => void; fieldHints?: string[] }) {
  const update = (i: number, patch: Partial<Condition>) => onChange(value.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const parseValue = (op: string, raw: string): unknown => {
    if (["in", "not_in", "between"].includes(op)) return raw.split(",").map((s) => s.trim()).filter(Boolean);
    return raw;
  };
  return (
    <Stack spacing={1}>
      {value.length === 0 && <Typography variant="body2" color="text.secondary">No conditions — matches every event.</Typography>}
      {value.map((c, i) => (
        <Stack key={i} direction="row" spacing={1} alignItems="center">
          <TextField size="small" label="Field" value={c.field} onChange={(e) => update(i, { field: e.target.value })} sx={{ width: 220 }}
            slotProps={{ htmlInput: { list: "field-hints" } }} />
          <TextField size="small" select label="Operator" value={c.op} onChange={(e) => update(i, { op: e.target.value })} sx={{ width: 150 }}>
            {OPS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          {!["is_empty", "is_not_empty"].includes(c.op) && (
            <TextField size="small" label={["in", "not_in", "between"].includes(c.op) ? "Values (comma separated)" : "Value"}
              value={Array.isArray(c.value) ? (c.value as unknown[]).join(", ") : String(c.value ?? "")} onChange={(e) => update(i, { value: parseValue(c.op, e.target.value) })} sx={{ flex: 1 }} />
          )}
          <IconButton aria-label="Remove condition" onClick={() => onChange(value.filter((_, j) => j !== i))}><DeleteIcon fontSize="small" /></IconButton>
        </Stack>
      ))}
      <datalist id="field-hints">{fieldHints.map((f) => <option key={f} value={f} />)}</datalist>
      <Button size="small" onClick={() => onChange([...value, { field: "", op: "==", value: "" }])} sx={{ alignSelf: "flex-start" }}>Add condition</Button>
    </Stack>
  );
}
